/**
 * CreditsPanel — Nexus v29
 *
 * Unified credits panel. Rendered inline on the Billing page AND as a
 * modal from every "credits" entry point (header, sidebar, profile,
 * chat "Recargar créditos" button, etc.).
 *
 * Sections:
 *   1. "Tus créditos" — balance, ring, progress bar
 *   2. "Comprar créditos" — 6-package grid with 500cr featured
 *   3. "Resumen de compra" — recap + Pagar ahora
 *
 * Post-payment flow:
 *   • Success → <PaymentSuccessModal /> → "Continuar construyendo"
 *   • Cancel  → <PaymentFailedModal /> → "Intentar nuevamente" / "Cancelar"
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Coins, Info, Lock, Shield, Star, Zap } from 'lucide-react';
import { apiService } from '../../services/api';
import { useCredits } from '../../contexts/CreditsContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

type Package = Awaited<ReturnType<typeof apiService.getBillingPackages>>['packages'][number];

// Baseline used to compute the progress bar. Anchored at the largest
// standard purchase so 5000 credits ≈ 100% full.
const BASELINE_CREDITS = 200;

type Props = {
  /** Optional path to return to after Stripe redirects (defaults to
   *  wherever the panel was opened from). */
  returnTo?: string | null;
  /** Callback when the user clicks the close button or completes flow. */
  onClose?: () => void;
  /** Inline (page) vs modal styling. */
  variant?: 'inline' | 'modal';
};

export const CreditsPanel: React.FC<Props> = ({
  returnTo, onClose, variant = 'inline',
}) => {
  const { balance, refresh, setBalanceOptimistic } = useCredits();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [packages, setPackages] = useState<Package[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getBillingPackages();
        if (cancelled) return;
        setPackages(data.packages);
        // Default select the featured (most popular) package.
        const featured = data.packages.find(p => p.featured) || data.packages[2];
        setSelectedId(featured?.id ?? data.packages[0]?.id ?? null);
      } catch {
        toast.error('No pudimos cargar los paquetes de créditos');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selected = useMemo(
    () => packages.find(p => p.id === selectedId) || packages[0],
    [packages, selectedId],
  );

  const currentBalance = balance ?? 0;
  const baseline = Math.max(BASELINE_CREDITS, currentBalance || BASELINE_CREDITS);
  const percentRemaining = Math.min(100, Math.round((currentBalance / baseline) * 100));

  const handlePay = useCallback(async () => {
    if (!selected || redirecting) return;
    // ─── Admin Pass ────────────────────────────────────────────
    // Admins never hit Stripe. Credit their account instantly via
    // self-recharge endpoint and refresh balance across the app.
    if (isAdmin) {
      try {
        setRedirecting(true);
        const r = await apiService.adminSelfRecharge(selected.id);
        // Optimistically apply the authoritative balance_after to
        // avoid stale state from concurrent poll refreshes.
        if (typeof r?.balance_after === 'number') {
          setBalanceOptimistic(r.balance_after);
        } else {
          await refresh({ force: true });
        }
        toast.success(
          `Admin Pass · ${r.credits_added} créditos acreditados (saldo: ${r.balance_after})`,
          { duration: 4500 },
        );
        setRedirecting(false);
        if (onClose) onClose();
      } catch (e: any) {
        const msg = e?.response?.data?.detail || 'No pudimos aplicar el Admin Pass. Intenta nuevamente.';
        toast.error(msg);
        setRedirecting(false);
      }
      return;
    }
    try {
      setRedirecting(true);
      const result = await apiService.createCheckoutSession(
        selected.id,
        { returnTo: returnTo || undefined },
      );
      // Redirect to Stripe Checkout hosted page.
      window.location.href = result.checkout_url;
    } catch (e: any) {
      const msg = e?.response?.data?.detail || 'No pudimos iniciar el pago. Intenta nuevamente.';
      toast.error(msg);
      setRedirecting(false);
    }
  }, [selected, redirecting, returnTo, isAdmin, refresh, setBalanceOptimistic, onClose]);

  return (
    <div
      data-testid="credits-panel"
      className={
        variant === 'modal'
          ? 'w-full max-w-4xl mx-auto text-slate-100'
          : 'w-full text-slate-100'
      }
    >
      {/* ══════════════════ 1. Tus créditos ══════════════════ */}
      <section
        data-testid="credits-balance-card"
        className="relative overflow-hidden rounded-2xl border border-cyan-400/25 bg-slate-950/60 backdrop-blur-xl p-6 sm:p-8"
      >
        <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

        <div className="flex items-start justify-between mb-6">
          <span className="text-[11px] tracking-[0.22em] font-semibold text-cyan-300 uppercase">
            Tus créditos
          </span>
          <button
            type="button"
            className="text-slate-500 hover:text-cyan-300 transition-colors"
            title="1 crédito = $0.20 USD"
          >
            <Info size={16} />
          </button>
        </div>

        <div className="flex items-center gap-6">
          {/* Ring */}
          <div className="relative shrink-0 h-24 w-24 sm:h-28 sm:w-28">
            <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
              <circle cx="50" cy="50" r="44" fill="none"
                stroke="rgba(6,182,212,0.12)" strokeWidth="6" />
              <circle
                cx="50" cy="50" r="44" fill="none"
                stroke="#22d3ee" strokeWidth="6" strokeLinecap="round"
                strokeDasharray={`${(2 * Math.PI * 44) * (percentRemaining / 100)} ${2 * Math.PI * 44}`}
                style={{ transition: 'stroke-dasharray 900ms ease' }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <Coins size={28} className="text-cyan-300/90" />
            </div>
          </div>

          {/* Balance */}
          <div className="min-w-0">
            <div
              data-testid="credits-balance-value"
              className="text-5xl sm:text-6xl font-semibold tracking-tight leading-none"
            >
              {balance === null ? '···' : currentBalance.toLocaleString('es-ES')}
            </div>
            <div className="mt-2 text-sm text-slate-400">
              créditos disponibles
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-6">
          <div className="h-2 rounded-full bg-slate-800/80 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-cyan-300 transition-all duration-700"
              style={{ width: `${percentRemaining}%` }}
              data-testid="credits-progress-bar"
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs">
            <span className="text-slate-500">
              Te quedan {currentBalance.toLocaleString('es-ES')} créditos para llegar a 0
            </span>
            <span className="text-cyan-300 font-medium">
              {percentRemaining}% restante
            </span>
          </div>
        </div>
      </section>

      {/* ══════════════════ 2. Comprar créditos ══════════════════ */}
      <section className="mt-6">
        <div className="mb-4 flex items-center gap-2">
          <Coins size={16} className="text-cyan-300" />
          <span className="text-[11px] tracking-[0.22em] font-semibold text-cyan-300 uppercase">
            Comprar créditos
          </span>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-40 rounded-2xl bg-slate-900/60 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {packages.map(pkg => {
              const isSelected = pkg.id === selectedId;
              const isFeatured = !!pkg.featured;
              return (
                <button
                  key={pkg.id}
                  type="button"
                  onClick={() => setSelectedId(pkg.id)}
                  data-testid={`package-card-${pkg.credits}`}
                  className={[
                    'group relative rounded-2xl p-5 text-left transition-all',
                    'border backdrop-blur-md',
                    isSelected
                      ? 'border-cyan-400 bg-cyan-500/[0.06] shadow-[0_0_0_1px_rgba(34,211,238,0.35),0_20px_50px_-15px_rgba(34,211,238,0.35)]'
                      : 'border-slate-800/70 bg-slate-950/40 hover:border-cyan-400/40 hover:bg-slate-900/50',
                    isFeatured && !isSelected ? 'ring-1 ring-cyan-400/30' : '',
                  ].join(' ')}
                >
                  {isFeatured && (
                    <div
                      data-testid="badge-most-popular"
                      className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-full bg-cyan-400 text-slate-950 text-[10px] tracking-[0.12em] font-semibold uppercase px-3 py-1 shadow-[0_10px_25px_-8px_rgba(34,211,238,0.6)]"
                    >
                      <Star size={11} fill="currentColor" strokeWidth={0} />
                      Más popular
                    </div>
                  )}

                  {isSelected && (
                    <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-cyan-400 text-slate-950 flex items-center justify-center">
                      <Check size={14} strokeWidth={3} />
                    </div>
                  )}

                  <div className={
                    'mb-3 h-9 w-9 rounded-lg flex items-center justify-center ' +
                    (isSelected ? 'bg-cyan-400/15 text-cyan-300' : 'bg-slate-800/60 text-cyan-300/80')
                  }>
                    <Coins size={18} />
                  </div>

                  <div className="text-2xl font-semibold tracking-tight">
                    {pkg.credits.toLocaleString('es-ES')}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">créditos</div>

                  <div className="mt-4">
                    <span className="text-xl font-semibold text-cyan-300">
                      ${pkg.price_usd.toFixed(0)}.00
                    </span>
                    <span className="text-xs text-slate-500 ml-1">USD</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* ══════════════════ 3. Resumen de compra ══════════════════ */}
      {selected && (
        <section
          data-testid="purchase-summary"
          className="mt-6 rounded-2xl border border-slate-800/70 bg-slate-950/40 backdrop-blur-xl p-6 sm:p-7"
        >
          <div className="mb-5 text-[11px] tracking-[0.22em] font-semibold text-cyan-300 uppercase">
            Resumen de compra
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            {/* Card graphic */}
            <div className="hidden md:flex justify-center">
              <div className="h-28 w-44 rounded-xl border border-cyan-400/30 bg-gradient-to-br from-slate-900/80 to-slate-950/60 p-3 relative overflow-hidden">
                <div className="h-6 w-8 rounded-sm bg-cyan-400/40" />
                <div className="mt-6 flex gap-1.5">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-1.5 w-6 rounded-full bg-cyan-300/25" />
                  ))}
                </div>
                <Coins size={16} className="absolute bottom-2 right-2 text-cyan-300/50" />
              </div>
            </div>

            {/* Details */}
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">
                Obtendrás
              </div>
              <div className="text-2xl font-semibold text-cyan-300">
                {selected.credits.toLocaleString('es-ES')} <span className="text-slate-300 font-normal">créditos</span>
              </div>

              <div className="mt-5 text-xs uppercase tracking-widest text-slate-500 mb-1">
                Total a pagar
              </div>
              <div className="text-3xl font-semibold text-cyan-300">
                ${selected.price_usd.toFixed(2)} <span className="text-sm text-slate-400 font-normal">USD</span>
              </div>
            </div>

            {/* Assurances */}
            <div className="space-y-3 text-sm text-slate-300">
              <div className="flex items-center gap-2">
                <Lock size={15} className="text-cyan-300/80" />
                {isAdmin ? 'Admin Pass · sin pago real' : 'Pago 100% seguro con Stripe'}
              </div>
              <div className="flex items-center gap-2">
                <Zap size={15} className="text-cyan-300/80" />
                {isAdmin ? 'Acreditación instantánea' : 'Acceso inmediato a tus créditos'}
              </div>
              <div className="flex items-center gap-2">
                <Shield size={15} className="text-cyan-300/80" />
                {isAdmin ? 'Uso interno · audit log activo' : 'Sin cargos ocultos'}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handlePay}
            disabled={redirecting || !selected}
            data-testid="pay-now-button"
            className={[
              'mt-6 w-full flex items-center justify-center gap-2 rounded-xl',
              'text-slate-950 font-semibold tracking-wide',
              'bg-gradient-to-r from-cyan-400 to-cyan-300',
              'py-4 text-base',
              'shadow-[0_20px_50px_-15px_rgba(34,211,238,0.6)]',
              'transition-transform active:translate-y-[1px]',
              redirecting ? 'opacity-70 cursor-wait' : 'hover:from-cyan-300 hover:to-cyan-200',
            ].join(' ')}
          >
            {redirecting
              ? (isAdmin ? 'Aplicando Admin Pass…' : 'Abriendo Stripe…')
              : (isAdmin ? 'Aplicar Admin Pass →' : 'Pagar ahora →')}
          </button>

          <div className="mt-3 text-center text-[11px] text-slate-500 flex items-center justify-center gap-1.5">
            <Shield size={12} />
            Tus créditos se activan al instante después del pago.
          </div>
        </section>
      )}

      {onClose && variant === 'modal' && (
        <div className="mt-6 text-center">
          <button
            onClick={onClose}
            className="text-sm text-slate-500 hover:text-slate-300"
            data-testid="credits-panel-close"
          >
            Cerrar
          </button>
        </div>
      )}
    </div>
  );
};

export default CreditsPanel;
